<template>
  <div class="gam-map">
    <MapCityBanner v-if="city && isMapOutsideCity" />
    <div
      :class="[
        'gmap-container',
        zoomClassName,
        {
          'add-mode': isEditingLocation || mapStore.seedPickerMode,
          'editing-location': isEditingLocation,
          'location-mode': isEditingLocation,
          'gambit-mode': mapStore.seedPickerMode,
          'proposal-mode': mapStore.proposalMode,
          'pinpointing-location': isChoosingCoordinate || mapStore.seedPickerMode,
        },
      ]"
    >
      <GoogleMap
        id="g-map"
        ref="gMap"
        v-bind="getGMapOptions"
        @mousemove="GetCoordinates"
        @dragstart="startDrag"
        @dragend="endDrag"
        @idle="onMapIdle"
        @zoom_changed="onMapZoomChanged"
        @heading_changed="onHeadingChanged"
        @click="handleClick"
      >
        <template #default="{ ready }">
          <template v-if="ready">
            <template v-if="showCursor && cursorLocation">
              <CustomMarker :options="{ position: cursorLocation.coordinates, anchorPoint: 'CENTER' }">
                <gam-add-pin
                  :is-dragging="isDragging"
                  :pin="cursorLocation"
                  :label="
                    isValidLocation(cursorLocation.coordinates) ? formatLatLng(cursorLocation.coordinates) : 'X, Y'
                  "
                  is-unclickable
                />
              </CustomMarker>
            </template>

            <CustomMarker
              v-if="(isEditingLocation || proposalVirtualPinpoint) && pinpoint && !isLocationSelected"
              :options="{ position: pinpoint, anchorPoint: 'CENTER' }"
            >
              <gam-add-pin :pin="cursorLocation" :is-dragging="isDragging" is-pinned is-unclickable />
            </CustomMarker>

            <CustomMarker v-if="userCenter" :options="{ position: userCenter, anchorPoint: 'CENTER', zIndex: 2 }">
              <div class="user-center" />
            </CustomMarker>

            <CustomMarker
              v-if="searchPin && !isPickerMode"
              :options="{ position: searchPin, anchorPoint: 'CENTER', zIndex: 3 }"
            >
              <div class="search-add-spot" @click="handleAddSpotPin">
                <span class="search-add-spot-icon">+</span>
                <span class="search-add-spot-label">{{ localize('locations.map.search.add_spot') }}</span>
              </div>
            </CustomMarker>

            <CustomMarker
              v-if="searchPin && isPickerMode"
              :options="{ position: searchPin, anchorPoint: 'CENTER', zIndex: 3 }"
            >
              <div class="search-pin-indicator">
                <GamIcon :name="GamIconName.LOCATION_EMPTY" size="20px" />
              </div>
            </CustomMarker>

            <CustomMarker
              v-if="seedPlantCoords"
              :options="{ position: seedPlantCoords, anchorPoint: 'BOTTOM_CENTER', zIndex: 9999 }"
            >
              <div class="seed-plant-marker" @click.stop="emits('plant')">
                <div class="seed-plant-icon">
                  <img src="@/ui/icons/gambit.svg" class="seed-plant-img" alt="" />
                </div>
              </div>
            </CustomMarker>

            <!-- Map markers (unified: puzzle markers + start/reveal position markers) -->
            <template v-for="marker in mapPuzzleStore.markers" :key="'marker-' + marker.id">
              <CustomMarker
                v-if="marker.puzzleType === 'walk_start'"
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9090,
                }"
              >
                <div class="walk-session-marker walk-origin-marker">
                  <span class="trace-square trace-square-origin" />
                </div>
              </CustomMarker>
              <CustomMarker
                v-else-if="marker.puzzleType === 'walk_reveal'"
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9090,
                }"
              >
                <div class="walk-session-marker walk-origin-marker">
                  <span class="trace-square trace-square-reveal" />
                </div>
              </CustomMarker>
              <CustomMarker
                v-else-if="marker.puzzleType === 'walk_wrong'"
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9100,
                }"
              >
                <div class="walk-session-marker walk-session-solved walk-session-wrong-noclk">
                  <span class="trace-square trace-square-mine-private" />
                </div>
              </CustomMarker>
              <CustomMarker
                v-else-if="marker.puzzleType === 'walk_not_submitted'"
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9100,
                }"
              >
                <div class="walk-session-marker walk-session-solved walk-session-wrong-noclk">
                  <span class="trace-square trace-square-not-submitted" />
                </div>
              </CustomMarker>
              <CustomMarker
                v-else-if="marker.puzzleType === 'walk_corrected'"
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9100,
                }"
              >
                <div class="walk-session-marker walk-session-solved" @click="handleMapMarkerClick(marker)">
                  <span class="trace-square trace-square-mine-private trace-corrected">
                    <span class="corrected-dot" />
                  </span>
                </div>
              </CustomMarker>
              <CustomMarker
                v-else
                :options="{
                  position: { lat: marker.lat, lng: marker.lng },
                  anchorPoint: 'CENTER',
                  zIndex: 9100,
                }"
              >
                <div class="walk-session-marker walk-session-solved" @click="handleMapMarkerClick(marker)">
                  <span class="trace-square trace-square-solved-by-me" />
                </div>
              </CustomMarker>
            </template>

            <!-- Trace markers (colored squares) -->
            <template v-for="trace in mapPuzzleStore.traces" :key="'tr-' + trace.id">
              <!-- My trace solved by others: faded pink square -->
              <template v-if="trace.isMine && trace.status === 'trace_solved'">
                <Circle
                  :options="{
                    center: { lat: trace.lat, lng: trace.lng },
                    radius: 50,
                    fillOpacity: 0,
                    strokeWeight: 0,
                    clickable: true,
                  }"
                  @click="handleMyTraceClick(trace)"
                />
                <Circle
                  v-for="ring in PROXIMITY_RINGS"
                  :key="'tr-ring-' + trace.id + '-' + ring.radius"
                  :options="getProximityRingOptions({ lat: trace.lat, lng: trace.lng }, ring, TRACE_PINK)"
                />
                <CustomMarker
                  :options="{
                    position: { lat: trace.lat, lng: trace.lng },
                    anchorPoint: 'CENTER',
                    zIndex: 9050,
                  }"
                >
                  <div class="walk-session-marker walk-trace-marker" @click="handleMyTraceClick(trace)">
                    <svg v-if="trace.expiresAt" class="walk-timer-ring" width="44" height="44" viewBox="0 0 44 44">
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="screenColor"
                        stroke-opacity="0.15"
                        stroke-width="3"
                      />
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="screenColor"
                        stroke-opacity="0.7"
                        stroke-width="3"
                        stroke-linecap="round"
                        :stroke-dasharray="timerCircumference"
                        :stroke-dashoffset="getTraceTimerOffset(trace)"
                        transform="rotate(90 22 22) scale(-1,1) translate(-44,0)"
                      />
                    </svg>
                    <span class="trace-square trace-square-mine-solved" />
                  </div>
                </CustomMarker>
              </template>

              <!-- My unsolved trace: emoji badge, clickable from anywhere for preview -->
              <template v-else-if="trace.isMine && trace.status === 'trace'">
                <Circle
                  :options="{
                    center: { lat: trace.lat, lng: trace.lng },
                    radius: 50,
                    fillOpacity: 0,
                    strokeWeight: 0,
                    clickable: true,
                  }"
                  @click="handleMyTraceClick(trace)"
                />
                <Circle
                  v-for="ring in PROXIMITY_RINGS"
                  :key="'tr-ring-' + trace.id + '-' + ring.radius"
                  :options="
                    getProximityRingOptions(
                      { lat: trace.lat, lng: trace.lng },
                      ring,
                      isTracePrivate(trace) ? TRACE_RED : TRACE_PINK,
                    )
                  "
                />
                <CustomMarker
                  :options="{ position: { lat: trace.lat, lng: trace.lng }, anchorPoint: 'CENTER', zIndex: 9150 }"
                >
                  <div class="walk-session-marker walk-trace-marker walk-trace-mine" @click="handleMyTraceClick(trace)">
                    <svg
                      v-if="trace.publicAt && new Date(trace.publicAt).getTime() > now"
                      class="walk-timer-ring"
                      width="44"
                      height="44"
                      viewBox="0 0 44 44"
                    >
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="TRACE_PINK"
                        stroke-opacity="0.15"
                        stroke-width="3"
                      />
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="TRACE_PINK"
                        stroke-opacity="0.7"
                        stroke-width="3"
                        stroke-linecap="round"
                        :stroke-dasharray="timerCircumference"
                        :stroke-dashoffset="getMyTraceTimerOffset(trace)"
                        transform="rotate(90 22 22) scale(-1,1) translate(-44,0)"
                      />
                    </svg>
                    <span
                      class="trace-square"
                      :class="isTracePrivate(trace) ? 'trace-square-mine-private' : 'trace-square-mine'"
                    />
                  </div>
                </CustomMarker>
              </template>

              <!-- Others' unsolved trace: proximity required -->
              <template v-else>
                <Circle
                  :options="{
                    center: { lat: trace.lat, lng: trace.lng },
                    radius: 50,
                    fillOpacity: 0,
                    strokeWeight: 0,
                    clickable: true,
                  }"
                  @click="handleTraceMarkerClick(trace)"
                />
                <Circle
                  v-for="ring in PROXIMITY_RINGS"
                  :key="'tr-ring-' + trace.id + '-' + ring.radius"
                  :options="getProximityRingOptions({ lat: trace.lat, lng: trace.lng }, ring, TRACE_YELLOW)"
                />
                <CustomMarker
                  :options="{ position: { lat: trace.lat, lng: trace.lng }, anchorPoint: 'CENTER', zIndex: 9050 }"
                >
                  <div class="walk-session-marker walk-trace-marker">
                    <svg
                      v-if="trace.status === 'trace_solved' && trace.expiresAt"
                      class="walk-timer-ring"
                      width="44"
                      height="44"
                      viewBox="0 0 44 44"
                    >
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="screenColor"
                        stroke-opacity="0.15"
                        stroke-width="3"
                      />
                      <circle
                        cx="22"
                        cy="22"
                        r="20"
                        fill="none"
                        :stroke="screenColor"
                        stroke-opacity="0.7"
                        stroke-width="3"
                        stroke-linecap="round"
                        :stroke-dasharray="timerCircumference"
                        :stroke-dashoffset="getTraceTimerOffset(trace)"
                        transform="rotate(90 22 22) scale(-1,1) translate(-44,0)"
                      />
                    </svg>
                    <span
                      class="trace-square trace-square-other"
                      :class="{ 'trace-square-solved': trace.status === 'trace_solved' }"
                    />
                  </div>
                </CustomMarker>
              </template>
            </template>

            <Rectangle v-if="showCityBounds && cityBoundsRectOptions" :options="cityBoundsRectOptions" />
          </template>
        </template>
      </GoogleMap>
    </div>

    <PuzzleModal
      v-if="mapPuzzleStore.showTracePuzzle && frozenTracePuzzleData"
      :puzzle-data="frozenTracePuzzleData"
      :walk-mode="true"
      :view-only="isViewOnlyTrace"
      :walk-result="null"
      :walk-submit-state="'idle'"
      :board-reset-key="traceBoardResetKey"
      :puzzle-index="1"
      :puzzle-total="0"
      :fading="false"
      trace-mode
      :is-my-trace="isMyTrace"
      :is-trace-private="isMyTracePrivate"
      :is-solved-replay="isSolvedReplay"
      :trace-phase="tracePhase"
      :trace-retry-loading="traceRetryLoading"
      @submit-move="handleTraceMoveSubmit"
      @keep-walking="handleTraceClose"
      @retry-proximity="handleTraceRetryProximity"
      @play-replay="handleTracePlayReplay"
      @trace-retry="handleTraceRetryCta"
      @update-location="() => {}"
      @solved="() => {}"
    />
  </div>
</template>

<script setup lang="ts">
import { GeoDataSource, type GeoCoords, type MapLocationDto } from '@/core/data/location/location.interface';
import { MapLocationType } from '@/core/data/location/location.type';
import { gambits, localize } from '@/core/gambits';
import { formatLatLng, isValidLocation } from '@/core/helpers/list.helper';
import { haversineDistance } from '@/core/helpers/location.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import { useFormStore } from '@/stores/form.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { DEFAULT_RADIUS, useMapStore } from '@/stores/map.store';
import { useMapPuzzleStore } from '@/stores/mapPuzzle.store';
import { incomingMarkers, outgoingMarkers, updatedMarkers, useMarkerStore } from '@/stores/marker.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useTableStore } from '@/stores/table.store';
import { useWalkStore } from '@/stores/walk.store';
import GamAddPin from '@/views/components/gam-map/GamAddPin.vue';
import MapCityBanner from '@/views/components/gam-map/MapCityBanner.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { isOutsidePlayingCity } from '@/views/composables/hooks/useCitySwitchFlow';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import { requestLocationFor } from '@/views/composables/hooks/useLocationAsk';
import { useMarkerLocation } from '@/views/composables/hooks/useMarkerLocation';
import type { PuzzleData, TracePhase } from '@/views/composables/hooks/usePuzzleEngine';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import { type GamMapType } from '@/views/composables/models/components/GamMap';
import { type LocationFormProps, type LocationValidator } from '@/views/composables/models/form.interface';
import PuzzleModal from '@/views/main/puzzle/PuzzleModal.vue';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { Circle, CustomMarker, GoogleMap, Rectangle } from 'vue3-google-map';
import LatLngBounds = google.maps.LatLngBounds;
import LatLng = google.maps.LatLng;

const FALLBACK_LOCATION = {
  lat: 0,
  lng: 0,
};

const DEFAULT_ZOOM = 13;

const USE_CITY_BOUNDS = import.meta.env.VITE_USE_CITY_BOUNDS === 'true';

defineProps<GamMapType>();

const showCursor = ref<boolean>(false);

const mapPuzzleStore = useMapPuzzleStore();
const walkStore = useWalkStore();

const { absorb: absorbGhostClick } = useGhostClickGuard();

const COLLECT_RADIUS_M = 50;
const isMarkerProcessing = ref(false);

const isViewOnlyTrace = ref(false);
const isSolvedReplay = ref(false);

// --- Trace puzzle state ---
const TRACE_SOLVED_TTL_MS = 60 * 60 * 1000;
const frozenTracePuzzleData = ref<PuzzleData | null>(null);
const traceBoardResetKey = ref(0);
const isMyTrace = ref(false);
const isMyTracePrivate = ref(false);
const tracePhase = ref<TracePhase | null>(null);
const traceRetryLoading = ref(false);
const activeRedMarkerId = ref<string | null>(null);

function getTraceTimerOffset(trace: { expiresAt: string | null }) {
  if (!trace.expiresAt) return 0;
  const expires = new Date(trace.expiresAt).getTime();
  const remaining = Math.max(0, (expires - now.value) / TRACE_SOLVED_TTL_MS);
  return timerCircumference * (1 - remaining);
}

const MY_TRACE_TTL_MS = 24 * 60 * 60 * 1000;

function getMyTraceTimerOffset(trace: { publicAt: string | null }) {
  if (!trace.publicAt) return 0;
  const publicTime = new Date(trace.publicAt).getTime();
  const remaining = Math.max(0, (publicTime - now.value) / MY_TRACE_TTL_MS);
  return timerCircumference * (1 - remaining);
}

function isTracePrivate(trace: { publicAt: string | null }) {
  if (!trace.publicAt) return false;
  return new Date(trace.publicAt).getTime() > now.value;
}

async function handleMyTraceClick(trace: { id: string; lat: number; lng: number; publicAt: string | null }) {
  if (isMarkerProcessing.value) return;

  isMarkerProcessing.value = true;
  isMyTracePrivate.value = isTracePrivate(trace);
  try {
    if (geolocationStore.isLocationPermissionGranted) {
      geolocationStore.requestFreshPosition();
    }

    const result = await mapPuzzleStore.previewMyTrace(trace.id);
    if (result) {
      isViewOnlyTrace.value = true;
      isMyTrace.value = true;
      isSolvedReplay.value = false;
      tracePhase.value = 'play_cta';
      frozenTracePuzzleData.value = {
        id: result.puzzle.id,
        fen: result.puzzle.fen,
        setupMove: result.puzzle.setupMove,
        solutionMove: result.puzzle.solutionMove || '',
        rating: result.puzzle.rating,
        hasSolved: true,
        pendingPlayCta: true,
        replayMove: result.submittedMoveUci || undefined,
      };
      mapPuzzleStore.showTracePuzzle = true;
      absorbGhostClick();
    }
  } finally {
    isMarkerProcessing.value = false;
  }
}

async function handleTraceMarkerClick(trace: {
  id: string;
  lat: number;
  lng: number;
  isMine?: boolean;
  publicAt?: string | null;
}) {
  if (isMarkerProcessing.value) return;

  if (!geolocationStore.isLocationPermissionGranted) {
    requestLocationFor('traces', () => {
      void handleTraceMarkerClick(trace);
    });
    return;
  }

  isMarkerProcessing.value = true;
  try {
    const loc = await getMarkerLocation();
    if (!loc) return;

    if (!trace.isMine) {
      const dist = haversineDistance(loc, { lat: trace.lat, lng: trace.lng });
      if (dist > COLLECT_RADIUS_M) {
        useToast().info(localize('puzzle.get_closer'), { id: 'walk-proximity' });
        return;
      }
    }

    const result = await mapPuzzleStore.openTrace(trace.id, loc.lat, loc.lng);
    if (result) {
      const solutionMoves = [result.puzzle.setupMove, result.puzzle.solutionMove];
      frozenTracePuzzleData.value = {
        id: result.puzzle.id,
        fen: result.puzzle.fen,
        setupMove: solutionMoves[0],
        solutionMove: solutionMoves[1] || '',
        rating: result.puzzle.rating,
        hasSolved: false,
      };
      isMyTrace.value = result.isMine ?? false;
      tracePhase.value = 'interactive';
    }
  } finally {
    isMarkerProcessing.value = false;
  }
}

async function handleTraceMoveSubmit(moveUci: string) {
  const trace = mapPuzzleStore.activeTrace;
  if (!trace) return;

  const result = await mapPuzzleStore.solveTrace(trace.id, moveUci);

  if (!result) return;

  tracePhase.value = result.isCorrect ? 'correct' : 'wrong';
  if (result.isCorrect) {
    if (activeRedMarkerId.value) {
      mapPuzzleStore.flipMarkerToGreen(activeRedMarkerId.value);
    }
    reloadTraces();
  }
}

function handleTraceClose() {
  absorbGhostClick();
  mapPuzzleStore.closeTracePuzzle();
  frozenTracePuzzleData.value = null;
  isViewOnlyTrace.value = false;
  isMyTrace.value = false;
  isMyTracePrivate.value = false;
  isSolvedReplay.value = false;
  tracePhase.value = null;
  traceRetryLoading.value = false;
  activeRedMarkerId.value = null;
}

const TRACE_LOCATION_FRESH_MS = 60_000;

async function handleTraceRetryProximity() {
  traceRetryLoading.value = true;
  try {
    const preview = mapPuzzleStore.previewedTrace;
    if (!preview) {
      useToast().info(localize('puzzle.get_closer'), { id: 'walk-proximity' });
      return;
    }

    const traceData = mapPuzzleStore.traces.find((t) => t.id === preview.id);
    if (!traceData) {
      useToast().info(localize('puzzle.get_closer'), { id: 'walk-proximity' });
      return;
    }

    const cached = geolocationStore.userLocation?.location;
    const age = Date.now() - new Date(geolocationStore.userLocation?.updatedAt || 0).getTime();

    if (!traceData.isMine) {
      if (!geolocationStore.isLocationPermissionGranted) {
        requestLocationFor('traces', () => {
          void handleTraceRetryProximity();
        });
        return;
      }

      const cachedIsNearby =
        cached && haversineDistance(cached, { lat: traceData.lat, lng: traceData.lng }) <= COLLECT_RADIUS_M;

      if (!cached || !cachedIsNearby || age > TRACE_LOCATION_FRESH_MS) {
        await geolocationStore.requestFreshPosition();
      }

      const loc = geolocationStore.userLocation?.location;
      if (!loc) {
        useToast().info(localize('puzzle.get_closer'), { id: 'walk-proximity' });
        return;
      }

      const dist = haversineDistance(loc, { lat: traceData.lat, lng: traceData.lng });
      if (dist > COLLECT_RADIUS_M) {
        useToast().info(localize('puzzle.get_closer'), { id: 'walk-proximity' });
        return;
      }
    }

    const loc = cached ?? geolocationStore.userLocation?.location ?? { lat: traceData.lat, lng: traceData.lng };
    const result = await mapPuzzleStore.openTrace(preview.id, loc.lat, loc.lng);
    if (result) {
      const solutionMoves = [result.puzzle.setupMove, result.puzzle.solutionMove];
      frozenTracePuzzleData.value = {
        id: result.puzzle.id,
        fen: result.puzzle.fen,
        setupMove: solutionMoves[0],
        solutionMove: solutionMoves[1] || '',
        rating: result.puzzle.rating,
        hasSolved: false,
        skipSetupAnimation: true,
      };
      tracePhase.value = 'interactive';
      traceBoardResetKey.value++;
    }
  } finally {
    traceRetryLoading.value = false;
  }
}

function handleTracePlayReplay(wasCorrect: boolean) {
  if (isSolvedReplay.value) {
    tracePhase.value = 'correct';
    return;
  }
  tracePhase.value = wasCorrect ? 'correct' : 'wrong';
  if (wasCorrect) reloadTraces();
}

async function handleTraceRetryCta() {
  const at = mapPuzzleStore.activeTrace;
  if (at) {
    frozenTracePuzzleData.value = {
      id: at.puzzle.id,
      fen: at.puzzle.fen,
      setupMove: at.puzzle.setupMove,
      solutionMove: at.puzzle.solutionMove || '',
      rating: at.puzzle.rating,
      hasSolved: false,
      skipSetupAnimation: true,
    };
    tracePhase.value = 'interactive';
    traceBoardResetKey.value++;
    return;
  }
  await handleTraceRetryProximity();
}

function applyWalkMarkerFilter() {
  const isMainWalk = walkStore.session?.status === 'active' || walkStore.session?.status === 'completed';
  const showAll = walkStore.showWalkMarkers;

  if (showAll) {
    walkStore.showWalkMarkers = false;
  } else if (isMainWalk) {
    mapPuzzleStore.markers = mapPuzzleStore.markers.filter(
      (m) => !m.puzzleType.startsWith('walk_') || m.puzzleType === 'walk_start' || m.puzzleType === 'walk_reveal',
    );
  } else {
    mapPuzzleStore.markers = mapPuzzleStore.markers.filter((m) => !m.puzzleType.startsWith('walk_'));
  }

  if (route.query.fit_markers === 'true' && mapPuzzleStore.markers.length > 0 && gMap.value?.map) {
    geolocationStore.setShouldPanToChangedUserLocation(false);
    const bounds = new google.maps.LatLngBounds();
    mapPuzzleStore.markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    gMap.value.map.fitBounds(bounds, 40);
    hasFitBounds.value = true;
    nextTick(() => {
      router.replace({ ...route, query: { ...route.query, fit_markers: undefined } });
    });
  }
}

function reloadTraces() {
  const loc = geolocationStore.userLocation?.location;
  if (loc) {
    mapPuzzleStore.loadTraces(loc.lat, loc.lng);
  }
}

const reloadTracesDebounced = debounce(() => reloadTraces(), 5000, { trailing: true });

async function handleMapMarkerClick(marker: {
  id: string;
  fen: string;
  solutionUci: string;
  rating: number;
  puzzleType: string;
  submittedMoveUci?: string | null;
  traceId?: string | null;
}) {
  if (isMarkerProcessing.value) return;

  isMarkerProcessing.value = true;
  try {
    const [setupMove, ...rest] = marker.solutionUci.split(' ');

    if (marker.puzzleType === 'walk_wrong' && marker.traceId) {
      activeRedMarkerId.value = marker.id;
      isMyTracePrivate.value = true;

      const traceResult = await mapPuzzleStore.openTraceById(marker.traceId);
      mapPuzzleStore.activeTrace = traceResult;

      frozenTracePuzzleData.value = {
        id: traceResult?.puzzle?.id ?? 0,
        fen: marker.fen,
        setupMove: setupMove || '',
        solutionMove: rest.join(' '),
        rating: marker.rating,
        hasSolved: true,
        pendingPlayCta: true,
        replayMove: marker.submittedMoveUci || undefined,
      };
      isViewOnlyTrace.value = true;
      isMyTrace.value = true;
      isSolvedReplay.value = false;
      tracePhase.value = 'play_cta';
      mapPuzzleStore.showTracePuzzle = true;
    } else {
      frozenTracePuzzleData.value = {
        id: 0,
        fen: marker.fen,
        setupMove: setupMove || '',
        solutionMove: rest.join(' '),
        rating: marker.rating,
        hasSolved: true,
        pendingPlayCta: true,
      };
      isViewOnlyTrace.value = true;
      isMyTrace.value = false;
      isSolvedReplay.value = true;
      tracePhase.value = 'play_cta';
      mapPuzzleStore.showTracePuzzle = true;
    }
  } finally {
    isMarkerProcessing.value = false;
  }
}

// --- Proximity rings & timer for walk session markers ---

const PROXIMITY_RINGS = [
  { radius: 50, opacity: 0.02 },
  { radius: 38, opacity: 0.04 },
  { radius: 26, opacity: 0.06 },
  { radius: 14, opacity: 0.1 },
];

const timerCircumference = 2 * Math.PI * 20;
const now = ref(Date.now());

let timerTick: ReturnType<typeof setInterval> | null = null;

const TRACE_PINK = 'oklch(0.7818 0.2418 355)';
const TRACE_RED = '#e74c3c';
const TRACE_YELLOW = '#f5c542';

const screenColor = computed(() => '#000000');

function getProximityRingOptions(
  center: { lat: number; lng: number },
  ring: { radius: number; opacity: number },
  color: string,
) {
  return {
    center,
    radius: ring.radius,
    fillColor: color,
    fillOpacity: ring.opacity,
    strokeWeight: 0,
    clickable: false,
  };
}

const router = useRouter();
const route = useRoute();

const navigationStore = useNavigationStore();
const { navigateToCitySignal } = storeToRefs(navigationStore);
const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);

const isMapOutsideCity = computed(
  () => mapStore.isLocationValid && isOutsidePlayingCity(mapStore.currentLocation, city.value),
);

const showCityBounds = computed(() => {
  return route.query.show_city_bounds === 'true';
});

const cityBoundsRectOptions = computed(() => {
  if (!city.value?.viewportHigh || !city.value?.viewportLow) return null;
  return {
    bounds: {
      north: city.value.viewportHigh.lat,
      south: city.value.viewportLow.lat,
      east: city.value.viewportHigh.lng,
      west: city.value.viewportLow.lng,
    },
    strokeColor: '#FF0000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#FF0000',
    fillOpacity: 0.15,
    clickable: false,
  };
});

const isEditingLocation = computed(() => ['add_location', 'edit_location'].includes(String(route.query.side_panel)));
const isChoosingCoordinate = computed(() => isEditingLocation.value);

const emits = defineEmits([GamComponentsEmits.UPDATE, 'plant']);
const listId = GamListId.MARKERS;

const mapStore = useMapStore();
const { addMode, cursorLocation, satelliteMode, currentRadius, searchPin, showAllSpots, userToggledSpots } =
  storeToRefs(mapStore);

const geolocationStore = useGeolocationStore();
const { userLocation, shouldPanToChangedUserLocation } = storeToRefs(geolocationStore);
const { getLocation: getMarkerLocation } = useMarkerLocation();

const legalRepositioningInterval = ref<number>();

watch(
  () => route.query,
  (query) => {
    if (!['add_location', 'edit_location'].includes(String(query.side_panel)) && !mapStore.proposalMode) {
      mapStore.setAddMode(false);
      return;
    }

    mapStore.setAddMode(true);
  },
);

const applyProposalRestriction = () => {
  const map = gMap.value?.map;
  if (!map || !mapStore.pendingProposalCenter) return false;
  const center = mapStore.pendingProposalCenter;
  const radiusM = mapStore.pendingProposalRadiusM ?? 50;
  const offsetLat = radiusM / 111320;
  const offsetLng = radiusM / (111320 * Math.cos((center.lat * Math.PI) / 180));
  map.setOptions({
    restriction: {
      latLngBounds: {
        north: center.lat + offsetLat,
        south: center.lat - offsetLat,
        east: center.lng + offsetLng,
        west: center.lng - offsetLng,
      },
      strictBounds: true,
    },
    minZoom: SEED_PICKER_MIN_ZOOM,
  });
  map.panTo(center);
  mapStore.pendingProposalCenter = null;
  mapStore.pendingProposalRadiusM = null;
  return true;
};

watch(
  () => mapStore.proposalMode,
  (active, wasActive) => {
    mapStore.setAddMode(active);
    if (active) {
      applyProposalRestriction();
    }
    if (!active && wasActive) {
      const map = gMap.value?.map;
      if (map) {
        map.setOptions({ restriction: null, minZoom: null });
      }
      const prevLocation = mapStore.mapProposalLocation;
      if (prevLocation && prevLocation.id !== mapStore.VIRTUAL_LOCATION_ID) {
        gambits.locationService.updateMarker(prevLocation, undefined);
      }
    }
  },
);

const spotQueryId = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const setPinSelected = (id: string, selected: boolean) => {
  const pin = document.getElementById(`pin-${id}`);
  if (!pin) return;
  pin.classList.toggle('selected', selected);
  pin.classList.toggle('lc-selected', selected);
};

watch(
  () => route.query.spot,
  (spot, previous) => {
    const nextId = spotQueryId(spot);
    const prevId = spotQueryId(previous);
    if (nextId === prevId) return;

    if (prevId && prevId !== nextId && nextId) {
      setPinSelected(prevId, false);
    }

    // Closing the overlay keeps the ring on the pin that was just open.
    if (!nextId && prevId) {
      mapStore.setRingedSpot(prevId);
      setPinSelected(prevId, true);
      return;
    }

    if (!nextId) return;

    if (mapStore.ringedSpotId && mapStore.ringedSpotId !== nextId) {
      setPinSelected(mapStore.ringedSpotId, false);
    }
    mapStore.setRingedSpot(null);
    setPinSelected(nextId, true);
  },
);

watch(
  () => mapStore.ringedSpotId,
  (id, previous) => {
    const openId = spotQueryId(route.query.spot);
    if (previous && previous !== id && previous !== openId) {
      setPinSelected(previous, false);
    }
    if (id) setPinSelected(id, true);
  },
);

watch(navigateToCitySignal, handleNavigateToCity);

const addLocationFormStore = useFormStore<LocationFormProps, LocationValidator>(FormId.ADD_LOCATION)();
const editLocationFormStore = useFormStore<LocationFormProps, LocationValidator>(FormId.EDIT_LOCATION)();
const { gamForm: addLocationFormRef } = storeToRefs(addLocationFormStore);
const { gamForm: editLocationFormRef } = storeToRefs(editLocationFormStore);

const isLocationSelected = computed(() => {
  return !!route.query.spot;
});

const proposalVirtualPinpoint = computed(() => {
  return mapStore.proposalMode && mapStore.mapProposalLocation?.id === mapStore.VIRTUAL_LOCATION_ID;
});

const isPickerMode = computed(() => mapStore.proposalMode);

// TODO: Refactor to remove coupling with the global form state
let suppressCursorClick = false;

function handleCursorClick(point: GeoCoords) {
  if (suppressCursorClick) {
    suppressCursorClick = false;
    return;
  }

  router.push({ ...route, query: { ...route.query, spot: undefined } });

  if (addLocationFormRef.value) {
    (addLocationFormRef.value.form as any).pinpoint = point;
  }

  if (editLocationFormRef.value) {
    (editLocationFormRef.value.form as any).pinpoint = point;
  }
}

const pinpoint = computed(() => {
  const { side_panel } = route.query;

  if (side_panel === 'add_location' && (addLocationFormRef?.value?.form as any)?.pinpoint) {
    return (addLocationFormRef.value?.form as any).pinpoint;
  }

  if (side_panel === 'edit_location' && (editLocationFormRef.value?.form as any)?.pinpoint) {
    return (editLocationFormRef.value?.form as any).pinpoint;
  }

  if (mapStore.proposalMode && mapStore.mapProposalLocation?.id === mapStore.VIRTUAL_LOCATION_ID) {
    return mapStore.mapProposalLocation.coordinates;
  }

  if (mapStore.seedPickerMode && mapStore.selectedSeedSpot?.id === mapStore.VIRTUAL_LOCATION_ID) {
    return mapStore.selectedSeedSpot.coordinates;
  }

  return null;
});

const seedPlantCoords = computed(() => {
  if (!mapStore.seedPickerMode || !mapStore.selectedSeedSpot) return null;
  return mapStore.selectedSeedSpot.coordinates;
});

const gMap = ref<InstanceType<typeof GoogleMap>>();

const panUpForProposalCard = () => {
  if (!mapStore.proposalMode || mapStore.checkInMode) return;
  gMap.value?.map?.panBy(0, 150);
};

const markerStore = useMarkerStore(listId)();
const { lastUpdated } = storeToRefs(markerStore);

const isMapInitialized = ref<boolean>(false);
const hasFitBounds = ref(false);
const isDragging = ref<boolean>(false);
const addCenter = ref<GeoCoords | null>(null);
const mapCenter = ref<GeoCoords>();
const mapZoom = ref<number>();

const initialZoom = mapStore.isLocationValid ? mapStore.currentZoom : undefined;
const initialCenter = mapStore.isLocationValid ? { ...mapStore.currentLocation } : undefined;
const isRestoringPosition = ref(mapStore.isLocationValid);

const tableStore = useTableStore<GamDropdownItem>(GamListId.ADD_LOCATION)();

async function geocodeCoordinates(coordinates: GeoCoords) {
  await tableStore.setSearchQuery({
    search: `${coordinates.lat}, ${coordinates.lng}`,
    preventLoad: false,
    selectFirstItem: true,
  });
}

function handleNavigateToCity() {
  if (!city.value?.coordinates) {
    return;
  }

  if (USE_CITY_BOUNDS && city.value.viewportHigh && city.value.viewportLow && typeof google !== 'undefined') {
    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(city.value.viewportLow.lat, city.value.viewportLow.lng),
      new google.maps.LatLng(city.value.viewportHigh.lat, city.value.viewportHigh.lng),
    );
    gMap.value?.map?.fitBounds(bounds);
  } else {
    gMap.value?.map?.panTo(city.value.coordinates);
  }
}

const mapCenterFromQueryParams = computed(() => {
  const { lat, lng } = route.query;
  if (!lat || !lng) {
    return gMap.value?.center;
  }

  const latNum = parseFloat(String(lat));
  const lngNum = parseFloat(String(lng));

  if (isNaN(latNum) || isNaN(lngNum)) {
    return gMap.value?.center;
  }

  return { lat: latNum, lng: lngNum };
});

watch(
  () => route.query.initialize_with_current_coords,
  (new_init_request, old_init_request) => {
    if (String(new_init_request) === 'true' && String(old_init_request) !== 'true') {
      const lat = String(route.query.lat);
      const lng = String(route.query.lng);

      handleCursorClick({ lat: parseFloat(lat), lng: parseFloat(lng) });

      nextTick(() => {
        router.replace({ ...route, query: { ...route.query, initialize_with_current_coords: undefined } });
      });
    }
  },
);

watch(
  () => route.query.fit_markers,
  (val) => {
    if (val !== 'true' || !gMap.value?.map) return;
    const marks = mapPuzzleStore.markers;
    if (marks.length === 0) return;

    geolocationStore.setShouldPanToChangedUserLocation(false);
    const bounds = new google.maps.LatLngBounds();
    marks.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    gMap.value.map.fitBounds(bounds, 40);
    hasFitBounds.value = true;

    nextTick(() => {
      router.replace({ ...route, query: { ...route.query, fit_markers: undefined } });
    });
  },
);

watch([isEditingLocation, isChoosingCoordinate], (currentState, previousState) => {
  const isChoosingCoordinate = currentState[1];
  const wasEditingLocation = previousState[0];

  if (!isChoosingCoordinate && isMobile.value) return;
  if (wasEditingLocation) return;

  const center = mapCenterFromQueryParams.value || gMap.value?.center;
  if (!center || typeof center.lat !== 'number' || typeof center.lng !== 'number') return;

  const coords = { lat: center.lat, lng: center.lng };

  setCursorLocation(coords);
  geocodeCoordinates(coords);
});

const handleAddSpotPin = () => {
  suppressCursorClick = true;
  setTimeout(() => {
    suppressCursorClick = false;
  }, 500);
  router.push({ ...route, query: { ...route.query, side_panel: 'add_location' } });
  nextTick(() => {
    mapStore.searchPin = null;
  });
};

const resolveSeedPinLocation = debounce(async (coords: GeoCoords) => {
  const result = await gambits.locationService.reverseGeocode(coords.lat, coords.lng);
  const loc = result?.data as any;
  const name = loc?.name || '';
  const address = loc?.address || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
  const resolvedCoords = loc?.coordinates || coords;

  mapStore.selectSeedSpot({
    mapLocationType: MapLocationType.LOCATION,
    id: mapStore.VIRTUAL_LOCATION_ID,
    coordinates: resolvedCoords,
    name,
    address,
  });
}, 300);

const handleSeedPickerMapClick = (coords: GeoCoords) => {
  const prev = mapStore.selectedSeedSpot;
  if (prev && prev.id !== mapStore.VIRTUAL_LOCATION_ID) {
    gambits.locationService.updateMarker(prev, undefined);
  }

  mapStore.selectSeedSpot({
    mapLocationType: MapLocationType.LOCATION,
    id: mapStore.VIRTUAL_LOCATION_ID,
    coordinates: coords,
    name: '',
    address: `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
  });

  resolveSeedPinLocation(coords);
};

const resolveProposalPinLocation = debounce(async (coords: GeoCoords) => {
  const result = await gambits.locationService.reverseGeocode(coords.lat, coords.lng);
  const loc = result?.data as any;
  const name = loc?.name || '';
  const address = loc?.address || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;

  const current = mapStore.mapProposalLocation;
  if (current && current.id === mapStore.VIRTUAL_LOCATION_ID) {
    current.name = name;
    current.address = address;
  }
}, 300);

const handleProposalMapClick = (coords: GeoCoords) => {
  const prev = mapStore.mapProposalLocation;
  if (prev && prev.id !== mapStore.VIRTUAL_LOCATION_ID) {
    gambits.locationService.updateMarker(prev, undefined);
  }

  gMap.value?.map?.panTo(coords);
  mapStore.setMapProposalLocation({
    mapLocationType: MapLocationType.LOCATION,
    id: mapStore.VIRTUAL_LOCATION_ID,
    coordinates: coords,
    name: '',
    address: `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
  });
  panUpForProposalCard();

  resolveProposalPinLocation(coords);
};

const handleClick = (e: MouseEvent & { latLng: any }) => {
  if (mapStore.editGambitId) {
    return;
  }

  if (mapStore.seedPickerMode && e.latLng && !isDragging.value) {
    const coords: GeoCoords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    handleSeedPickerMapClick(coords);
    return;
  }

  if (mapStore.proposalMode && !mapStore.checkInMode && e.latLng && !isDragging.value) {
    const coords: GeoCoords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    if (mapStore.walkFromReveal && mapStore.proposalConstraintCenter) {
      const dist = haversineDistance(mapStore.proposalConstraintCenter, coords);
      const maxDist = mapStore.proposalConstraintRadiusM ?? 50;
      if (dist > maxDist) {
        const toastKey =
          mapStore.walkSpotRole === 'reveal' ? 'walk.proximity.too_far_reveal' : 'walk.proximity.too_far_origin';
        useToast().info(localize(toastKey), { id: 'walk-too-far' });
        return;
      }
    }
    handleProposalMapClick(coords);
    return;
  }

  if (mapStore.proposalMode) {
    return;
  }

  if (mapStore.mapProposalLocation) {
    mapStore.setMapProposalLocation(null);
  }

  if (route.query.spot && !route.query.side_panel) {
    router.replace({ ...route, query: { ...route.query, spot: undefined } });
  } else if (mapStore.ringedSpotId && !route.query.side_panel && !isDragging.value) {
    mapStore.setRingedSpot(null);
  }

  if (!isEditingLocation.value || !e.latLng || isDragging.value) return;

  const coords: GeoCoords = { lat: e.latLng.lat(), lng: e.latLng.lng() };

  handleCursorClick(coords);
};

let prefetchedMarkers: ReturnType<typeof gambits.locationService.getMapLocations> | null = null;

onBeforeMount(async () => {
  await gambits.locationService.destroyMap();
  await markerStore.resetList();

  markerStore.setLoadCallback((listId, newCenter?: GeoCoords) => {
    if (newCenter) {
      mapCenter.value = newCenter;
      markerStore.setLocationParam(newCenter);
      return gambits.locationService.getMapLocations(listId, newCenter, currentRadius.value || DEFAULT_RADIUS);
    }

    if (route.query.lat && route.query.lng) {
      mapCenter.value = { lat: parseFloat(String(route.query.lat)), lng: parseFloat(String(route.query.lng)) };
    } else if (mapStore.isLocationValid) {
      mapCenter.value = { ...mapStore.currentLocation };
    } else if (gMap?.value?.map?.getCenter()) {
      const center = gMap?.value?.map?.getCenter() as LatLng;
      mapCenter.value = { lat: center?.lat(), lng: center?.lng() };
    } else if (city.value?.coordinates) {
      mapCenter.value = city.value.coordinates;
    } else {
      mapCenter.value = FALLBACK_LOCATION;
    }

    markerStore.setLocationParam(mapCenter.value);
    return gambits.locationService.getMapLocations(listId, mapCenter.value, currentRadius.value || DEFAULT_RADIUS);
  });
});

onMounted(async () => {
  // @ts-ignore
  window.gMap = gMap.value;

  if (mapStore.isLocationValid) {
    const center = { ...mapStore.currentLocation };
    prefetchedMarkers = gambits.locationService.getMapLocations(
      GamListId.MARKERS,
      center,
      mapStore.currentRadius || DEFAULT_RADIUS,
    );
  }

  handleBoundsChanged();
  markerStore.loadItems({ loadOnInit: true });
  legalRepositioningInterval.value = setInterval(repositionLegalElements, 500) as any as number;

  if (useAuthStore().isAuthenticated()) {
    if (!walkStore.showWalkMarkers) {
      applyWalkMarkerFilter();
    }
    walkStore.loadActiveSession().then(() => {
      applyWalkMarkerFilter();
    });
    reloadTraces();
  }

  timerTick = setInterval(() => {
    now.value = Date.now();
    mapPuzzleStore.checkExpiration();
  }, 1000);
});

const initMap = async () => {
  if (!isClustererInitialized.value) {
    await gambits.locationService.getMarkerCluster({
      map: gMap.value?.map,
      items: [],
      onClickMarker,
    });
    isClustererInitialized.value = true;
  }

  const queryLat = route.query.lat ? parseFloat(String(route.query.lat)) : null;
  const queryLng = route.query.lng ? parseFloat(String(route.query.lng)) : null;
  const queryZoom = route.query.zoom ? Number(route.query.zoom) : null;

  if (hasFitBounds.value) {
    isRestoringPosition.value = false;
  } else if (route.query.fit_markers === 'true') {
    if (mapPuzzleStore.markers.length > 0 && !walkStore.showWalkMarkers) {
      geolocationStore.setShouldPanToChangedUserLocation(false);
      const bounds = new google.maps.LatLngBounds();
      mapPuzzleStore.markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      gMap.value?.map?.fitBounds(bounds, 40);
      nextTick(() => {
        router.replace({ ...route, query: { ...route.query, fit_markers: undefined } });
        isRestoringPosition.value = false;
      });
    }
  } else if (queryLat != null && queryLng != null && !isNaN(queryLat) && !isNaN(queryLng)) {
    geolocationStore.setShouldPanToChangedUserLocation(false);
    const coords = { lat: queryLat, lng: queryLng };
    gMap.value?.map?.panTo(coords);
    gMap.value?.map?.setZoom(queryZoom || DEFAULT_ZOOM);
    mapStore.setCurrentLocation({ ...coords, zoom: queryZoom || DEFAULT_ZOOM });
    nextTick(() => {
      isRestoringPosition.value = false;
    });
  } else if (mapStore.isLocationValid) {
    if (userLocation.value?.source === GeoDataSource.BROWSER_API) {
      geolocationStore.setShouldPanToChangedUserLocation(false);
    }
    gMap.value?.map?.panTo(mapStore.currentLocation);
    gMap.value?.map?.setZoom(mapStore.currentZoom);
    nextTick(() => {
      isRestoringPosition.value = false;
    });
  } else if (city.value?.coordinates) {
    updateAddCenter(city.value.coordinates, DEFAULT_ZOOM);
  } else {
    mapStore.updateMapBounds(getMapBounds());
  }
  isMapInitialized.value = true;

  if (mapStore.proposalMode && mapStore.pendingProposalCenter) {
    applyProposalRestriction();
  }

  const mapBounds = getMapBounds();
  if (mapBounds) {
    mapStore.updateMapBounds(mapBounds);
    const northEast = mapBounds.getNorthEast();
    const mc = getMapCenter()!;
    const radius = google.maps.geometry.spherical.computeDistanceBetween(mc, northEast);
    mapStore.setNewRadius(radius);
  }

  if (prefetchedMarkers) {
    const prefetch = prefetchedMarkers;
    prefetchedMarkers = null;

    const result = await prefetch;
    if (result?.data?.length) {
      markerStore.setLocationParam(mapStore.currentLocation);
      markerStore.seedItems(result.data);
      return;
    }
  }

  const center = getMapCenter();
  if (center) {
    const c = { lat: center.lat(), lng: center.lng() };
    markerStore.setLocationParam(c);
    markerStore.addUniqueItems(c);
  }
};

const onMapIdle = () => {
  if (!isMapInitialized.value) {
    void initMap();
  }
};

const getMapBounds = (): LatLngBounds | undefined => {
  return gMap.value?.map?.getBounds();
};

const getMapCenter = (): LatLng | undefined => {
  return gMap.value?.map?.getCenter();
};

const setCursorLocation = (coords: GeoCoords) => {
  mapStore.setCursorLocation({
    address: `${coords.lat}, ${coords.lng}`,
    coordinates: coords,
  });
};

const GetCoordinates = (event: google.maps.MapMouseEvent) => {
  if (!addMode.value) return;
  const coords: GeoCoords = {
    lat: event.latLng?.lat() || 0,
    lng: event.latLng?.lng() || 0,
  };
  setCursorLocation(coords);
  event.stop();
};

const startDrag = () => {
  isDragging.value = true;
  handleBoundsChanged.cancel?.();
  markerStore.addUniqueItems.cancel?.();
};

const endDrag = () => {
  setTimeout(() => {
    isDragging.value = false;
  }, 200);
  handleBoundsChanged();
};

const updateAddCenter = (center: GeoCoords | null, zoom?: number) => {
  addCenter.value = center;
  if (center) {
    markerStore.setLocationParam(center);
  }
  if (zoom) gMap.value?.map?.setZoom(zoom);
};

const repositionLegalElements = () => {
  const isRepositionNeeded = window.innerWidth < 976;

  if (!isRepositionNeeded) {
    document.getElementById('g-map')?.classList.add('is-legal-notice-repositioned');
    clearInterval(legalRepositioningInterval.value);

    return;
  }

  const logoEl = document.querySelector('#g-map > div.mapdiv > div > div.gm-style > div:nth-child(14) > div');
  if (logoEl) {
    // @ts-ignore
    logoEl.style = `
      position: absolute; left: 0; right: auto; top: 0px; bottom: auto; height: min-content;
    `;
  }

  const logoImg = document.querySelector(
    '#g-map > div.mapdiv > div > div.gm-style > div:nth-child(14) > div > a > div > img',
  );
  if (logoImg) {
    // @ts-ignore - ???
    logoImg.style = 'width: 50px; height: 20px;';
  }

  const gTermsEl = document.querySelector('#g-map > div.mapdiv > div > div.gm-style > div:nth-child(16) > div');
  if (gTermsEl) {
    // @ts-ignore - ???
    gTermsEl.style = `
      position: absolute;

      top: 2px;
      left: 50px;
      right: 0;
      bottom: auto;
      height: min-content;
      display: flex;
      alignItems: center;
      justifyContent: flex-end;
      filter: invert();
    `;
  }

  const kbdHelpEl = document.querySelector(
    '#g-map > div.mapdiv > div > div.gm-style > div:nth-child(16) > div > div:nth-child(1)',
  );
  if (kbdHelpEl) {
    // @ts-ignore - ???
    kbdHelpEl.style = 'display: none';
  }

  if (kbdHelpEl && gTermsEl && logoImg && logoEl) {
    clearInterval(legalRepositioningInterval.value);
    document.getElementById('g-map')?.classList.add('is-legal-notice-repositioned');
  }
};

const urlTimeout = ref<number>();

const handleBoundsChanged = debounce(
  () => {
    if (!isMapInitialized.value) {
      return;
    }

    handleBoundsChanged.cancel();

    const mapCenter = getMapCenter();
    const mapBounds = getMapBounds();

    if (!mapCenter || !mapBounds) return;

    mapStore.updateMapBounds(mapBounds);

    const northEast = mapBounds.getNorthEast();
    const radius = google.maps.geometry.spherical.computeDistanceBetween(mapCenter, northEast);
    mapStore.setNewRadius(radius);

    const center = {
      lat: mapCenter.lat(),
      lng: mapCenter.lng(),
    };

    const zoom = gMap.value?.map?.getZoom() || DEFAULT_ZOOM;

    nextTick(() => {
      markerStore.setLocationParam(center);
      markerStore.addUniqueItems(center);
    });

    if (urlTimeout.value) {
      clearTimeout(urlTimeout.value);
    }
    urlTimeout.value = setTimeout(() => {
      router.replace({
        ...route,
        query: { ...route.query, ...center, zoom },
      });
    }, 1000) as any as number;

    mapStore.setCurrentLocation({ ...center, zoom });

    reloadTracesDebounced();
  },
  128,
  { trailing: true },
);

onBeforeUnmount(() => {
  if (urlTimeout.value) {
    clearTimeout(urlTimeout.value);
  }
  if (timerTick) {
    clearInterval(timerTick);
    timerTick = null;
  }
  reloadTracesDebounced.cancel();
});

// TODO: mapStore location is the source of truth for the location
const userCenter = computed((): GeoCoords | null => {
  try {
    return userLocation.value?.location || null;
  } catch {
    return null;
  }
});

const onClickMarker = async (location: MapLocationDto): Promise<void> => {
  if (location.mapLocationType === MapLocationType.LOCATION) {
    emits(GamComponentsEmits.UPDATE, location.id);

    if (mapStore.seedPickerMode) {
      if (location.isVetted || (location.activeChessboardCount ?? 0) > 0) {
        useToast().info(localize('seed.picker.spot_has_board'), { id: 'seed-spot-has-board' });
        return;
      }
      const prev = mapStore.selectedSeedSpot;
      if (prev && prev.id !== mapStore.VIRTUAL_LOCATION_ID) {
        gambits.locationService.updateMarker(prev, undefined);
      }
      mapStore.selectSeedSpot(location);
      gambits.locationService.updateMarker(location, location.id);
      return;
    }

    if (mapStore.proposalMode) {
      if (mapStore.walkFromReveal && mapStore.proposalConstraintCenter) {
        const dist = haversineDistance(mapStore.proposalConstraintCenter, location.coordinates);
        const maxDist = mapStore.proposalConstraintRadiusM ?? 50;
        if (dist > maxDist) {
          const toastKey =
            mapStore.walkSpotRole === 'reveal' ? 'walk.proximity.too_far_reveal' : 'walk.proximity.too_far_origin';
          useToast().info(localize(toastKey), { id: 'walk-too-far' });
          return;
        }
      }
      gMap.value?.map?.panTo(location.coordinates);
      panUpForProposalCard();
      const prevLocation = mapStore.mapProposalLocation;
      if (prevLocation && prevLocation.id !== mapStore.VIRTUAL_LOCATION_ID) {
        gambits.locationService.updateMarker(prevLocation, undefined);
      }
      mapStore.setMapProposalLocation(location);
      gambits.locationService.updateMarker(location, location.id);
      return;
    }

    if (geolocationStore.isLocationPermissionGranted) {
      gambits.geolocationService.getCurrentPosition();
    }

    router.push({ ...route, query: { ...route.query, spot: location.id } });
  } else if (location.mapLocationType === MapLocationType.CLUSTER) {
    const zoom = gMap.value?.map?.getZoom();
    if (!zoom) return;

    gMap.value?.map?.setCenter(location.coordinates);
    gMap.value?.map?.setZoom(zoom + 2);
  }
};

const onClickChessboard = (location: MapLocationDto): void => {
  if (mapStore.proposalMode) return;

  if (geolocationStore.isLocationPermissionGranted) {
    gambits.geolocationService.getCurrentPosition();
  }

  router.push({ ...route, query: { ...route.query, spot: location.id } });
};

const zoomClassName = computed(() => {
  if (typeof mapZoom.value !== 'number') {
    return 'zoom-default';
  }

  if (mapZoom.value < 12) {
    return 'zoom-nano';
  }

  if (mapZoom.value < 14) {
    return 'zoom-tiny';
  }

  return 'zoom-default';
});

const SPOTS_AUTO_ZOOM = 14;

const handleZoomChanged = () => {
  const zoom = gMap.value?.map?.getZoom();
  mapZoom.value = Number(zoom);
  mapStore.currentZoom = Number(zoom);

  if (Number(zoom) >= SPOTS_AUTO_ZOOM && !showAllSpots.value) {
    showAllSpots.value = true;
  } else if (Number(zoom) < SPOTS_AUTO_ZOOM && showAllSpots.value && !userToggledSpots.value) {
    showAllSpots.value = false;
  }
};

const onMapZoomChanged = () => {
  handleZoomChanged();
  handleBoundsChanged();
};

const onHeadingChanged = () => {
  const heading = gMap.value?.map?.getHeading() ?? 0;
  mapStore.mapHeading = heading;
};

const getGMapOptions = computed((): any => {
  return {
    ...gambits.configService.getGMapConfig(),
    ...gambits.configService.getMapZoom(),
    ...(initialZoom != null && { zoom: initialZoom }),
    center: addCenter.value || initialCenter || mapCenter.value,
    apiKey: gambits.configService.getGMapApiKey(),
    mapId: gambits.configService.getGMapMapId(),
    mapTypeId: satelliteMode.value ? 'satellite' : 'roadmap',
    isFractionalZoomEnabled: true,
    rotateControl: true,
    tiltControl: false,
    mapTypeControl: false,
  };
});

const SEED_PICKER_NEARBY_METERS = 50;
const SEED_PICKER_MIN_ZOOM = 16;

watch(
  () => mapStore.seedPickerMode,
  (active) => {
    const map = gMap.value?.map;
    if (!map) return;

    if (active) {
      const loc = geolocationStore.userLocation?.location;
      if (loc) {
        const offsetLat = SEED_PICKER_NEARBY_METERS / 111320;
        const offsetLng = SEED_PICKER_NEARBY_METERS / (111320 * Math.cos((loc.lat * Math.PI) / 180));
        map.setOptions({
          restriction: {
            latLngBounds: {
              north: loc.lat + offsetLat,
              south: loc.lat - offsetLat,
              east: loc.lng + offsetLng,
              west: loc.lng - offsetLng,
            },
            strictBounds: true,
          },
          minZoom: SEED_PICKER_MIN_ZOOM,
        });
      }
    } else {
      map.setOptions({ restriction: null, minZoom: null });
    }
  },
);

const isClustererInitialized = ref<boolean>(false);

watch(lastUpdated, async () => {
  if (!isClustererInitialized.value) return;

  const activeSpotId =
    typeof route.query.spot === 'string' ? route.query.spot : (mapStore.mapProposalLocation?.id ?? undefined);

  const safeOutgoing = activeSpotId ? outgoingMarkers.filter((m) => m.id !== activeSpotId) : outgoingMarkers;

  gambits.locationService.addItems(incomingMarkers, onClickMarker, showAllSpots.value, activeSpotId, onClickChessboard);
  gambits.locationService.removeItems(safeOutgoing);

  for (const item of updatedMarkers) {
    gambits.locationService.updateMarker(item, activeSpotId === item.id ? item.id : undefined);
  }
});

watch(showAllSpots, async (showAll) => {
  if (showAll) {
    await gambits.locationService.showInactiveMarkers();
  } else {
    const protectedId =
      typeof route.query.spot === 'string' ? route.query.spot : (mapStore.mapProposalLocation?.id ?? undefined);
    await gambits.locationService.hideInactiveMarkers(protectedId);
  }
  gambits.locationService.refreshBoardOnlyMarkers();
});

watch(
  () => [walkStore.startSpot?.id, walkStore.revealSpot?.id] as const,
  ([newStartId, newRevealId], [oldStartId, oldRevealId]) => {
    const affectedIds = new Set([newStartId, newRevealId, oldStartId, oldRevealId].filter(Boolean) as string[]);
    for (const id of affectedIds) {
      const locationData = gambits.locationService.getPinLocationData(id);
      if (locationData) {
        gambits.locationService.updateMarker(locationData, undefined);
      }
    }
  },
);

watch(userLocation, (newLocation) => {
  if (!shouldPanToChangedUserLocation.value) return;
  if (!newLocation?.location) return;

  // Only consume the flag when precise GPS data arrives or when GPS is unavailable.
  // This prevents the fast Cloudflare/IP response from eating the flag before the
  // slower browser GPS fix comes in — so the map will re-pan once GPS data arrives.
  if (newLocation.source === GeoDataSource.BROWSER_API || !geolocationStore.isLocationPermissionGranted) {
    geolocationStore.setShouldPanToChangedUserLocation(false);
  }

  gMap?.value?.map?.panTo(newLocation.location);
});

// onUnmounted(async () => {
//   await gambits.locationService.destroyMap();
//   markerStore.resetList();
// });
</script>

<style>
#g-map > div.mapdiv > div > div.gm-style > div:nth-child(16),
#g-map > div.mapdiv > div > div.gm-style > div:nth-child(14) {
  opacity: 0;
  transition: opacity 500ms;
}
#g-map.is-legal-notice-repositioned > div.mapdiv > div > div.gm-style > div:nth-child(16),
#g-map.is-legal-notice-repositioned > div.mapdiv > div > div.gm-style > div:nth-child(14) {
  opacity: 0.5;
}
</style>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.gam-map {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 100;

  .gmap-container {
    width: 100%;
    height: 100%;
    position: relative;

    &.add-mode {
      * {
        cursor: crosshair;
      }
    }

    #g-map {
      width: 100%;
      height: 100%;

      * {
        border: 0 !important;
      }
    }
  }
}

.user-center {
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background: var(--color-pink-30);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  position: relative;
  pointer-events: none;

  &:before {
    content: '';
    top: 9px;
    left: 9px;
    width: 6px;
    height: 6px;
    border-radius: 6px;
    background-color: var(--color-pink);
  }
}

.search-add-spot {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  cursor: pointer;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
  }

  .search-add-spot-icon {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
  }
}

.search-pin-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white-100);
  filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.6));
}

.seed-plant-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  pointer-events: auto;

  .seed-plant-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: var(--color-linear-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
      0 4px 16px 0 var(--color-pink-30),
      0 0 24px 4px var(--color-pink-20);
    animation: seed-plant-pulse 1.6s ease-in-out infinite;

    .seed-plant-img {
      width: 26px;
      height: 26px;
      filter: brightness(0) invert(1);
    }
  }
}

@keyframes seed-plant-pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow:
      0 4px 16px 0 var(--color-pink-30),
      0 0 24px 4px var(--color-pink-20);
  }

  50% {
    transform: scale(1.12);
    box-shadow:
      0 4px 24px 4px var(--color-pink-30),
      0 0 40px 12px var(--color-pink-20);
  }
}
</style>

<style lang="scss">
@use '@/ui/css/mixins' as *;
.location-mode {
  .gam-map-pin {
    pointer-events: none !important;
    .gam-map-pin-container {
      width: 6px !important;
      height: 6px !important;
    }

    .content {
      .icon-pin {
        width: 6px !important;
        height: 6px !important;
        opacity: 0 !important;
      }
    }
  }

  .location-circle {
    pointer-events: none !important;
    --lc-size: 6px !important;
    --lc-ring-offset: 0px !important;
    --lc-ring-width: 0px !important;
    --lc-badge-size: 0px !important;

    .lc-main {
      outline: none !important;
    }

    .lc-badge,
    .lc-name,
    .lc-temporal {
      display: none !important;
    }

    .lc-type-icon,
    img {
      display: none !important;
    }
  }

  .gam-pin-cluster-wrapper {
    width: 36px !important;
    height: 36px !important;

    .cluster-count {
      min-width: 16px !important;
      height: 16px !important;
      font-size: 8px !important;
    }
  }
}
.proposal-mode {
  .gam-map-pin {
    pointer-events: none !important;
  }
}

.walk-session-marker {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  pointer-events: none;
}

.walk-session-marker-emoji {
  font-size: 28px;
  line-height: 1;
  position: relative;
  z-index: 1;
}

.walk-session-marker-emoji-sub {
  font-size: 12px;
  position: absolute;
  bottom: 2px;
  right: 2px;
}

.walk-timer-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 44px;
  height: 44px;
}

.walk-session-marker-remove {
  position: absolute;
  top: -4px;
  left: -6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-30);
  color: var(--color-white-70);
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  z-index: 2;

  @include hover {
    background: var(--color-dark-600);
    color: var(--color-white);
  }
}

.walk-session-destination {
  background: transparent;
}

.walk-session-wrong {
  background: transparent;
}

.walk-session-solved {
  background: transparent;
  opacity: 0.85;
  cursor: pointer;
  pointer-events: auto;
}

.walk-session-wrong-noclk {
  cursor: default;
  pointer-events: none;
}

.walk-trace-marker {
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
}

.tap-preview-pulse {
  animation: tap-pulse 2.5s ease-in-out infinite;
  cursor: pointer;
}

.tap-confirm-label {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: var(--color-white-80);
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: var(--radius-small);
  white-space: nowrap;
}

@keyframes tap-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}
</style>
